import {
  Container, useDisclosure,
  Heading,
  Text,
  SimpleGrid,
  Button,
  Icon,
  Flex,
  HStack,
  VStack,
  Badge,
} from '@chakra-ui/react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import MainLayout from '@src/components/layouts/main';
import { useIsUserInRole, selectedDatabaseIdState } from '@transport-insights/uikit';
import EmptyState from '@src/components/shared/EmptyState';
import { FiPlus } from 'react-icons/fi';
import { useEffect, useState } from 'react';
import LoadingSpinner from '@src/components/shared/LoadingSpinner';
import { useConfirm } from '@src/hooks/useConfirm';
import { useSetRecoilState } from 'recoil';
import CustomReportsEdit from './CustomReportsEdit';
import { useCustomReportsList, useDeleteCustomReport, useUpdateDisplayOrder } from '../../context/custom-reports-api-hooks';

const getLocalDateString = () => {
  const date = new Date();

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

function SortableItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: '100%',
    cursor: 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

function CustomReportCard({ report, onEdit }) {
  const { confirm, ConfirmDialog } = useConfirm();
  const { mutateAsync: doDeleteCustomReport, isLoading: isDeleting } = useDeleteCustomReport();
  if (!report) return null;

  const handleEditReport = () => {
    onEdit(report);
  };

  const handleDeleteReport = async (reportId) => {
    const confirmed = await confirm({
      title: 'Are you sure you want to delete this report?',
      body: 'This action cannot be undone.',
      confirmButtonLabel: 'Delete Report',
    });

    if (confirmed) {
      await doDeleteCustomReport(reportId);
    }
  };

  const renderStatusBadge = () => {
    if (!report.publishDate) {
      return (
        <Badge colorScheme="gray">
          Draft
        </Badge>
      );
    }

    const today = getLocalDateString();

    if (report.publishDate > today) {
      return (
        <Badge colorScheme="blue">
          Scheduled
        </Badge>
      );
    }

    return (
      <Badge colorScheme="green">
        Published
      </Badge>
    );
  };

  return (
    <>
      <Flex
        key={report.id}
        mb={4}
        p={4}
        borderRadius="md"
        bg="white"
        boxShadow="base"
        flexGrow={1}
        direction="column"
        height="100%"
        justify="space-between"
      >
        <VStack align="flex-start" spacing={2} flexGrow={1}>
          {renderStatusBadge()}
          <Heading as="h2" size="sm">
            {report.name}
          </Heading>
          <Text fontSize="sm">{report.description}</Text>
        </VStack>
        <HStack justify="flex-end" spacing={2}>
          <Button
            size="xs"
            colorScheme="red"
            variant="ghost"
            onClick={() => handleDeleteReport(report.id)}
            isLoading={isDeleting}
          >
            Delete
          </Button>
          <Button
            size="xs"
            colorScheme="blue"
            variant="outline"
            onClick={() => handleEditReport(report)}
          >
            Edit
          </Button>
        </HStack>
      </Flex>
      <ConfirmDialog confirmButtonColor="red" />
    </>
  );
}

function CustomReports() {
  const hasDeveloperRole = useIsUserInRole('Developer');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedReport, setSelectedReport] = useState(null);
  const { data: customReports, isLoading } = useCustomReportsList();
  const { mutateAsync: doUpdateDisplayOrder } = useUpdateDisplayOrder();
  const [reportOrder, setReportOrder] = useState([]);
  const setSelectedDatabaseId = useSetRecoilState(selectedDatabaseIdState);

  // Set the database to National to avoid any display issues
  setSelectedDatabaseId(1000);

  // Build an array of all report names to use as validation for unique names when creating or editing reports
  const reportNames = customReports?.map((report) => report.name) || [];

  const handleEditReport = (report) => () => {
    setSelectedReport(report);
    onOpen();
  };

  const handleClose = () => {
    setSelectedReport(null);
    onClose();
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = reportOrder.indexOf(active.id);
    const newIndex = reportOrder.indexOf(over.id);

    const newOrder = arrayMove(reportOrder, oldIndex, newIndex);
    setReportOrder(newOrder); // Sync update

    const displayOrder = newOrder.map((id, index) => ({
      id,
      displayOrder: index,
    }));

    await doUpdateDisplayOrder(displayOrder); // Async update
  };

  useEffect(() => {
    if (customReports) {
      setReportOrder(customReports.map((r) => r.id));
    }
  }, [customReports]);

  if (!hasDeveloperRole) {
    return (
      <MainLayout backgroundColor="gray.50">
        <Container maxW="full" px={4} pb={4} flexGrow="1">
          <EmptyState
            title="No Access"
            message="Sorry, you do not have access to this page."
          />
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout backgroundColor="gray.50">
      <Container maxW="full" px={4} pb={4} flexGrow="1">
        <Heading as="h1">Custom Reports</Heading>
        <Text fontSize="md" color="gray.600" mt={2}>
          Create and manage your custom reports here. Drag and drop to reorder.
        </Text>
        <Container
          maxW="full"
          flexDirection="column"
          display="flex"
          height="100%"
          flexGrow={1}
          mt={4}
          p={0}
          position="relative"
        >
          {isLoading && (
            <Container
              maxW="full"
              flexDirection="column"
              display="flex"
              flexGrow={1}
            >
              <LoadingSpinner />
            </Container>
          )}
          {customReports?.length > 0 && (
          <SimpleGrid
            columns={{
              base: 1, sm: 1, md: 2, lg: 4, xl: 6,
            }}
            spacing={4}
            mt={8}
          >
            <Button
              variant="unstyled"
              width="100%"
              height="100%"
              border="1px dashed"
              borderColor="orange.500"
              borderRadius="base"
              display="flex"
              flexDir="column"
              color="orange.500"
              py={8}
              transition="all 0.5s ease"
              _hover={{ bg: 'orange.50' }}
              onClick={() => onOpen()}
            >
              <Icon as={FiPlus} boxSize={8} mb="4" />
              Add Custom Report
            </Button>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={reportOrder}
                strategy={rectSortingStrategy}
              >
                {reportOrder?.map((id) => {
                  const report = customReports.find((r) => r.id === id);
                  return (
                    <SortableItem key={id} id={id}>
                      <CustomReportCard
                        report={report}
                        onEdit={handleEditReport(report)}
                      />
                    </SortableItem>
                  );
                })}
              </SortableContext>
            </DndContext>
          </SimpleGrid>
          )}
          {customReports?.length === 0 && (
          <EmptyState
            title="No Custom Reports"
            message="You have not created any custom reports yet."
          >
            <Button
              mt={4}
              colorScheme="orange"
              size="md"
              onClick={onOpen}
            >
              Create Custom Report
            </Button>
          </EmptyState>
          )}
        </Container>
        <CustomReportsEdit
          isOpen={isOpen}
          onClose={handleClose}
          selectedReport={selectedReport}
          reportNames={reportNames}
        />
      </Container>
    </MainLayout>
  );
}

export default CustomReports;
